import CallEndOutlinedIcon from "@mui/icons-material/CallEndOutlined"
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined"
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined"
import VoicemailOutlinedIcon from "@mui/icons-material/VoicemailOutlined"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import Typography from "@mui/material/Typography"

import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { amdControls, amdStatusChip } from "./amd"

/**
 * Live detection state plus manual overrides, shown inside the call bar while
 * a Power Dialer session has answering-machine detection switched on.
 */
export default function AmdCallControls({
  status,
  onOverride,
  isOverriding = false,
  overrideError = "",
}) {
  const chip = amdStatusChip(status)
  const controls = amdControls(status)
  const showButtons =
    controls.canPerson ||
    controls.canText ||
    controls.canDrop ||
    controls.canHangup
  const detail = detailLine(status)

  return (
    <Column
      gap={1}
      sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: "divider" }}
    >
      <Row sx={{ alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 700, letterSpacing: "0.06em" }}
        >
          DETECTION
        </Typography>
        <Chip
          size="small"
          color={chip.color}
          label={chip.label}
          sx={{ fontWeight: 700 }}
        />
      </Row>
      {detail ? (
        <Typography variant="body2" color="text.secondary">
          {detail}
        </Typography>
      ) : null}
      {showButtons ? (
        <Row sx={{ gap: 0.75, flexWrap: "wrap" }}>
          {controls.canPerson ? (
            <Button
              size="small"
              variant="contained"
              color="success"
              disabled={isOverriding}
              startIcon={<PersonOutlineOutlinedIcon />}
              onClick={() => onOverride("person")}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              It&apos;s a person
            </Button>
          ) : null}
          {controls.canText ? (
            <Button
              size="small"
              variant="outlined"
              disabled={isOverriding}
              startIcon={<SmsOutlinedIcon />}
              onClick={() => onOverride("text")}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Text &amp; hang up
            </Button>
          ) : null}
          {controls.canDrop ? (
            <Button
              size="small"
              variant="outlined"
              disabled={isOverriding}
              startIcon={<VoicemailOutlinedIcon />}
              onClick={() => onOverride("voicemail")}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Leave voicemail
            </Button>
          ) : null}
          {controls.canHangup ? (
            <Button
              size="small"
              variant="outlined"
              color="error"
              disabled={isOverriding}
              startIcon={<CallEndOutlinedIcon />}
              onClick={() => onOverride("hangup")}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Hang up
            </Button>
          ) : null}
        </Row>
      ) : null}
      {overrideError ? (
        <Typography variant="body2" color="error">
          {overrideError}
        </Typography>
      ) : null}
    </Column>
  )
}

function detailLine(status) {
  if (!status) return ""
  if (status.amd_action === "texted") {
    return status.sms_template_name
      ? `Sent “${status.sms_template_name}”.`
      : "Text sent."
  }
  if (status.amd_action === "voicemail_dropped") {
    return status.voicemail_recording_name
      ? `Played “${status.voicemail_recording_name}”.`
      : "Voicemail left."
  }
  if (status.amd_action === "awaiting_agent") {
    return "Voicemail picked up. Choose what to do."
  }
  if (status.amd_result === "unknown") {
    return "Could not tell — connected you just in case."
  }
  return ""
}
